export type TechnicalCallStatus =
  | "RECORDED"
  | "IN_PROGRESS"
  | "WAITING"
  | "RESOLVED_PENDING_CONFIRMATION";

export type TechnicalCallImpact = "STOPPED" | "DEGRADED" | "RUNNING" | "UNKNOWN";

export type TechnicalBoardCall = {
  id: string;
  runId: string;
  lineId: string;
  lineCode: string;
  lineName: string;
  workOrderNo: string | null;
  productName: string | null;
  department: "ELECTRICAL" | "MECHANICAL";
  note: string;
  operatorName: string;
  createdAt: string;
  status: TechnicalCallStatus;
  impact: TechnicalCallImpact;
  notificationState: "NOT_CONFIGURED" | "PENDING" | "DELIVERED" | "FAILED";
  assignedTechnicianName: string | null;
  claimedAt: string | null;
  waitingReason: string | null;
  resolutionNote: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  version: number;
};

export type TechnicalBoardResponse = {
  items: TechnicalBoardCall[];
  onDutyTeam?: TechnicalBoardTeamMember[];
  generatedAt?: string;
};

export type TechnicalBoardTeamMember = {
  id: string;
  name: string;
  department: "ELECTRICAL" | "MECHANICAL";
  status: "AVAILABLE" | "IN_PROGRESS" | "OFF_DUTY";
  currentLineName: string | null;
};

export const refreshIntervalMs = 15_000;

export const statusLabel: Record<TechnicalCallStatus, string> = {
  RECORDED: "Yeni çağrı",
  IN_PROGRESS: "Müdahalede",
  WAITING: "Bekliyor",
  RESOLVED_PENDING_CONFIRMATION: "Doğrulama bekliyor",
};

export const impactLabel: Record<TechnicalCallImpact, string> = {
  STOPPED: "Üretim duruyor",
  DEGRADED: "Kısıtlı çalışıyor",
  RUNNING: "Üretim devam ediyor",
  UNKNOWN: "Üretim etkisi bilinmiyor",
};

export const teamStatusLabel: Record<TechnicalBoardTeamMember["status"], string> = {
  AVAILABLE: "Müsait",
  IN_PROGRESS: "Müdahalede",
  OFF_DUTY: "Vardiya dışında",
};

export function durationLabel(from: string, now: number) {
  const minutes = Math.max(0, Math.floor((now - Date.parse(from)) / 60_000));
  if (minutes < 60) return `${minutes} dk`;
  const hours = Math.floor(minutes / 60);
  return `${hours} sa ${minutes % 60} dk`;
}

export function clockLabel(value: Date) {
  return value.toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "Europe/Istanbul",
  });
}

export function dateLabel(value: Date) {
  return value.toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Istanbul",
  });
}

export function sortCalls(items: TechnicalBoardCall[]) {
  const impactWeight: Record<TechnicalCallImpact, number> = {
    STOPPED: 4,
    DEGRADED: 3,
    UNKNOWN: 2,
    RUNNING: 1,
  };
  return [...items].sort(
    (left, right) =>
      impactWeight[right.impact] - impactWeight[left.impact] ||
      Date.parse(left.createdAt) - Date.parse(right.createdAt),
  );
}

export function shortTime(value: string | null) {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Istanbul",
  });
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function nullableText(value: unknown) { return value === null || typeof value === "string"; }
function boardCall(value: unknown): value is TechnicalBoardCall {
  return record(value) && ["id","runId","lineId","lineCode","lineName","note","operatorName","createdAt"].every(key=>typeof value[key]==="string") &&
    ["workOrderNo","productName","assignedTechnicianName","claimedAt","waitingReason","resolutionNote","resolvedAt","closedAt"].every(key=>nullableText(value[key])) &&
    (value.department==="ELECTRICAL"||value.department==="MECHANICAL") &&
    (value.status==="RECORDED"||value.status==="IN_PROGRESS"||value.status==="WAITING"||value.status==="RESOLVED_PENDING_CONFIRMATION") &&
    (value.impact==="STOPPED"||value.impact==="DEGRADED"||value.impact==="RUNNING"||value.impact==="UNKNOWN") &&
    (value.notificationState==="NOT_CONFIGURED"||value.notificationState==="PENDING"||value.notificationState==="DELIVERED"||value.notificationState==="FAILED") &&
    typeof value.version==="number"&&Number.isInteger(value.version)&&value.version>0;
}
function boardMember(value: unknown): value is TechnicalBoardTeamMember {
  return record(value)&&typeof value.id==="string"&&typeof value.name==="string"&&
    (value.department==="ELECTRICAL"||value.department==="MECHANICAL")&&
    (value.status==="AVAILABLE"||value.status==="IN_PROGRESS"||value.status==="OFF_DUTY")&&nullableText(value.currentLineName);
}
export function parseTechnicalBoardResponse(value: unknown): TechnicalBoardResponse {
  if(!record(value)||!Array.isArray(value.items)||!value.items.every(boardCall)||
    (value.onDutyTeam!==undefined&&(!Array.isArray(value.onDutyTeam)||!value.onDutyTeam.every(boardMember)))||
    (value.generatedAt!==undefined&&typeof value.generatedAt!=="string"))throw new Error("Teknik çağrı yanıtı geçersiz.");
  return {items:value.items,onDutyTeam:value.onDutyTeam,generatedAt:value.generatedAt};
}
